
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useBooking } from '@/contexts/BookingContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

const ConfirmStep = () => {
  const { 
    selectedProvider, 
    selectedService, 
    selectedDate, 
    selectedTime,
    selectedStaff,
    bookingAddons,
    customNote,
    customImage,
    getTotalPrice,
    getTotalDuration,
    completeBooking,
    contactInfo,
    setContactInfo
  } = useBooking();
  
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToDataPolicy, setAgreedToDataPolicy] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  if (!selectedProvider || !selectedService || !selectedDate || !selectedTime) {
    return <p className="text-center text-chrona-gray-400">Bitte vervollständige die vorherigen Schritte.</p>;
  }
  
  const handleInputChange = (field: string, value: string) => {
    setContactInfo({...contactInfo, [field]: value});
  };
  
  const handleBookingSubmit = () => {
    // Verhindern von doppelten Klicks
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Timeout für den UX-Flow
    setTimeout(() => {
      // Referral code would be processed here
      completeBooking();
      // Reset submission state
      setIsSubmitting(false);
    }, 500);
  };
  
  const isFormValid = contactInfo.name && 
                     contactInfo.email && 
                     agreedToTerms && 
                     agreedToDataPolicy;
  
  return (
    <motion.div 
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="border-chrona-gray-800 bg-chrona-gray-900/60">
        <CardHeader className="border-b border-chrona-gray-800">
          <CardTitle className="text-chrona-text">Buchungsübersicht</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-4">
            <div className="flex justify-between pb-3 border-b border-chrona-gray-800">
              <span className="font-medium text-chrona-gray-300">Anbieter</span>
              <span className="text-chrona-text">{selectedProvider.name}</span>
            </div>
            <div className="flex justify-between pb-3 border-b border-chrona-gray-800">
              <span className="font-medium text-chrona-gray-300">Leistung</span>
              <span className="text-chrona-text">{selectedService.name}</span>
            </div>
            
            {selectedStaff && (
              <div className="flex justify-between pb-3 border-b border-chrona-gray-800">
                <span className="font-medium text-chrona-gray-300">Mitarbeiter</span>
                <span className="text-chrona-text">{selectedStaff.name}</span>
              </div>
            )}
            
            <div className="flex justify-between pb-3 border-b border-chrona-gray-800">
              <span className="font-medium text-chrona-gray-300">Datum & Zeit</span>
              <span className="text-chrona-text">
                {selectedDate.toLocaleDateString()} um {selectedTime} Uhr
              </span>
            </div>
            <div className="flex justify-between pb-3 border-b border-chrona-gray-800">
              <span className="font-medium text-chrona-gray-300">Dauer</span>
              <span className="text-chrona-text">{getTotalDuration()} Minuten</span>
            </div>
            
            {bookingAddons.length > 0 && (
              <div className="pb-3 border-b border-chrona-gray-800">
                <div className="flex justify-between mb-2">
                  <span className="font-medium text-chrona-gray-300">Zusatzoptionen</span>
                  <span className="text-chrona-text">{bookingAddons.length} ausgewählt</span>
                </div>
                <div className="space-y-2 pl-2">
                  {bookingAddons.map(addon => (
                    <div key={addon.id} className="flex justify-between text-sm">
                      <span className="text-chrona-gray-400">{addon.name}</span>
                      <span className="text-chrona-gray-300">{addon.price.toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {customNote && (
              <div className="pb-3 border-b border-chrona-gray-800">
                <span className="font-medium text-chrona-gray-300 block mb-1">Anmerkungen</span>
                <p className="text-chrona-gray-300 text-sm italic">"{customNote}"</p>
              </div>
            )}
            
            {customImage && (
              <div className="pb-3 border-b border-chrona-gray-800">
                <span className="font-medium text-chrona-gray-300 mb-2 block">Referenzbild</span>
                <div className="h-16 w-16 bg-chrona-gray-800 rounded-md overflow-hidden">
                  <img 
                    src={URL.createObjectURL(customImage)} 
                    alt="Referenz" 
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            )}
            
            <div className="flex justify-between font-bold text-lg">
              <span className="text-chrona-text">Gesamt</span>
              <span className="text-chrona-accent">{getTotalPrice().toFixed(2)} €</span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-chrona-gray-800 bg-chrona-gray-900/60">
        <CardHeader className="border-b border-chrona-gray-800">
          <CardTitle className="text-chrona-text">Deine Kontaktdaten</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-4">
            <div className="grid w-full gap-2">
              <Label htmlFor="name" className="text-sm font-medium text-chrona-gray-300">Name</Label>
              <Input 
                id="name"
                type="text"
                placeholder="Dein vollständiger Name"
                value={contactInfo.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text placeholder:text-chrona-gray-500"
              />
            </div>
            
            <div className="grid w-full gap-2">
              <Label htmlFor="email" className="text-sm font-medium text-chrona-gray-300">E-Mail</Label>
              <Input 
                id="email"
                type="email"
                placeholder="deine@email.de"
                value={contactInfo.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text placeholder:text-chrona-gray-500"
              />
            </div>
            
            <div className="grid w-full gap-2">
              <Label htmlFor="phone" className="text-sm font-medium text-chrona-gray-300">Telefonnummer</Label>
              <Input 
                id="phone"
                type="tel"
                placeholder="+49 123 4567890"
                value={contactInfo.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text placeholder:text-chrona-gray-500"
              />
              <span className="text-xs text-chrona-gray-500">Optional für Rückfragen</span>
            </div>
            
            <div className="grid w-full gap-2 pt-2 border-t border-chrona-gray-800">
              <Label htmlFor="referral" className="text-sm font-medium text-chrona-gray-300">Empfehlungscode (optional)</Label>
              <Input 
                id="referral"
                type="text"
                placeholder="z.B. FRIEND25"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                className="bg-chrona-gray-800 border-chrona-gray-700 text-chrona-text placeholder:text-chrona-gray-500"
              />
              <span className="text-xs text-chrona-gray-500">Gib einen Empfehlungscode ein, falls vorhanden</span>
            </div>
            
            <div className="flex items-start gap-2 mt-4">
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="terms" 
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(!!checked)}
                  className="data-[state=checked]:bg-chrona-accent"
                />
                <Label htmlFor="terms" className="text-sm text-chrona-gray-300">
                  Ich stimme den <Link to="/agb" className="text-chrona-accent hover:underline">AGB</Link> zu.
                </Label>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="datapolicy" 
                  checked={agreedToDataPolicy}
                  onCheckedChange={(checked) => setAgreedToDataPolicy(!!checked)}
                  className="data-[state=checked]:bg-chrona-accent"
                />
                <Label htmlFor="datapolicy" className="text-sm text-chrona-gray-300">
                  Ich stimme der <Link to="/datenschutz" className="text-chrona-accent hover:underline">Datenschutzerklärung</Link> zu.
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="border-t border-chrona-gray-800 pt-4">
          <Button
            className="w-full bg-chrona-accent hover:bg-chrona-accent-light text-chrona-text transition-all duration-200"
            disabled={!isFormValid || isSubmitting}
            onClick={handleBookingSubmit}
            data-booking-next
          >
            {isSubmitting ? 'Buchung wird verarbeitet...' : 'Verbindlich buchen'}
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default ConfirmStep;
